/**
 * System Announcement Modal
 * Displays system-wide announcements to users on login.
 */
import { useEffect } from 'react'
import { LuTriangleAlert, LuX } from 'react-icons/lu'
import { useAnnouncementStore } from '@stores/announcementStore'
import { Button, GlassCard } from '@components/ui'
import { useAuthStore } from '@stores/authStore'

export function SystemAnnouncementModal() {
    const { announcement, showModal, dismissModal, fetchAnnouncement, checkAndShowModal } =
        useAnnouncementStore()
    const { isAuthenticated } = useAuthStore()

    // Fetch announcement on mount and when user logs in
    useEffect(() => {
        if (isAuthenticated) {
            fetchAnnouncement().then(() => {
                checkAndShowModal()
            })
        }
    }, [isAuthenticated, fetchAnnouncement, checkAndShowModal])

    if (!showModal || !announcement?.is_enabled) {
        return null
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <GlassCard className="relative max-w-lg w-full p-0 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header with warning styling */}
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30 p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-amber-500/20">
                            <LuTriangleAlert className="w-6 h-6 text-amber-500" />
                        </div>
                        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                            System Notice
                        </h2>
                        <button
                            onClick={dismissModal}
                            className="ml-auto p-1.5 rounded-lg hover:bg-[var(--color-surface)] transition-colors"
                            aria-label="Close announcement"
                        >
                            <LuX className="w-5 h-5 text-[var(--color-text-secondary)]" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">
                        {announcement.message}
                    </p>
                </div>

                {/* Footer */}
                <div className="border-t border-[var(--color-border)] p-4 flex justify-end">
                    <Button onClick={dismissModal}>
                        I Understand
                    </Button>
                </div>
            </GlassCard>
        </div>
    )
}
